import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { of, switchMap, catchError, Observable } from 'rxjs';

import { AuthToken, AuthUser } from '@shared/signup/models';
import { DefaultPage } from '@shared/restaurant/enums';
import { SubscriptionState } from '@shared/recurly/enums';
import { UserAccountRole, UserLocationRole } from '@shared/user/enums';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AuthService } from '@tablesready/frontend-auth';
import { Credentials, GoogleCredentials } from '../types';
import { loginErrorAlerts } from '../constants';

@Injectable({ providedIn: 'root' })
export class LoginService {
  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly alertService: AlertsService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
  }

  login(credentials: Credentials): Observable<AuthUser> {
    return this.loginInternal(this.authService.login(credentials));
  }

  loginByToken(token: string, locationId: string): Observable<AuthUser> {
    return this.loginInternal(this.authService.loginByToken(token, locationId));
  }

  loginByGoogle(credentials: GoogleCredentials): Observable<AuthUser> {
    return this.loginInternal(this.authService.loginByGoogle(credentials));
  }

  setNavigatePath(user: AuthUser): string {
    const { queryParamMap } = this.activatedRoute.snapshot;
    let navigatePath = '/profile/details';

    if (user.location && !queryParamMap.get('navigateToProfile')) {
      const subscriptionState = user.location.recurly?.subscription_state;
      const needsBilling = subscriptionState === SubscriptionState.paused ||
        subscriptionState === SubscriptionState.expired;
      const isAccountAdmin = user.accountUser?.role === UserAccountRole.admin;
      const isLocationManager = user.accountUser?.role === UserAccountRole.user &&
        user.locationUser?.role === UserLocationRole.manager;

      if (needsBilling && (isAccountAdmin || isLocationManager)) {
        navigatePath = '/settings/location/billing';
      } else if (user.location.publicWaitlist.defaultPage === DefaultPage.reservation) {
        navigatePath = '/reservations';
      } else if (user.location.publicWaitlist.defaultPage === DefaultPage.floorPlan &&
        user.account.features.floorPlan
      ) {
        navigatePath = '/floorplan';
      } else {
        navigatePath = '/waitlist';
      }
    }

    if (queryParamMap.get('navigateToProfile')) {
      navigatePath = '/profile/accounts';
    }

    return navigatePath;
  }

  private loginInternal(method: Observable<AuthToken[]>): Observable<AuthUser> {
    return method.pipe(
      switchMap(() => this.authRestaurantService.load()),
      switchMap(() => this.authRestaurantService.buildOnce()),
      catchError((error: string) => {
        const alertOptions = {
          type: IAlertTypes.danger,
          ...(loginErrorAlerts[error] || loginErrorAlerts.default),
        };

        this.alertService.add(alertOptions);

        return of(null);
      }),
    );
  }
}
